import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import loadPdfjs from '../utils/loadPdfjs';
import { DOCUMENT } from '../constants/documentTheme';

// Rendering is capped so a huge scan can't freeze a phone.
const MAX_PAGES = 30;
const MAX_RENDER_WIDTH = 1200;

/**
 * Draws every page of a PDF onto canvases. Browser PDF viewers are unreliable
 * inside a page (in-app browsers show nothing, iOS shows only the first page),
 * so this keeps the preview identical everywhere.
 */
const PdfPreview = ({ url, title }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [truncatedAt, setTruncatedAt] = useState(null);
  const translateRef = useRef(t);
  translateRef.current = t;

  useEffect(() => {
    let cancelled = false;
    let pdfDocument = null;
    const container = containerRef.current;

    const render = async () => {
      try {
        setStatus('loading');
        setTruncatedAt(null);
        const pdfjs = await loadPdfjs();
        // eval off: uploads are untrusted, and font eval is how CVE-2024-4367 ran script.
        pdfDocument = await pdfjs.getDocument({ url, isEvalSupported: false }).promise;
        if (cancelled) return;

        container.replaceChildren();
        const targetWidth = Math.min(container.clientWidth || 800, MAX_RENDER_WIDTH);
        const pixelRatio = window.devicePixelRatio || 1;
        const pageCount = Math.min(pdfDocument.numPages, MAX_PAGES);

        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
          // eslint-disable-next-line no-await-in-loop
          const page = await pdfDocument.getPage(pageNumber);
          if (cancelled) return;

          const baseViewport = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (targetWidth / baseViewport.width) * pixelRatio });
          const canvas = window.document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.setAttribute(
            'aria-label',
            translateRef.current('documents.pdfPage', { page: pageNumber, total: pdfDocument.numPages })
          );
          canvas.setAttribute('role', 'img');
          Object.assign(canvas.style, {
            display: 'block',
            width: '100%',
            height: 'auto',
            margin: '0 auto 12px',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          });
          container.appendChild(canvas);

          // eslint-disable-next-line no-await-in-loop
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (pageNumber === 1 && !cancelled) setStatus('ready');
        }

        if (!cancelled && pdfDocument.numPages > MAX_PAGES) {
          setTruncatedAt(MAX_PAGES);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error rendering PDF preview:', error);
          setStatus('error');
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      if (pdfDocument) pdfDocument.destroy();
    };
  }, [url]);

  return (
    <Box
      data-testid="document-preview-pdf"
      aria-label={title}
      sx={{
        bgcolor: DOCUMENT.rule,
        p: { xs: 1, sm: 2 },
        maxHeight: { xs: 'calc(100vh - 140px)', sm: '75vh' },
        overflowY: 'auto',
      }}
    >
      {status === 'loading' && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      )}
      {status === 'error' && (
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, py: 4, textAlign: 'center' }}>
          {t('documents.previewFailed')}
        </Typography>
      )}
      <Box ref={containerRef} sx={{ maxWidth: MAX_RENDER_WIDTH, mx: 'auto' }} />
      {truncatedAt && (
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12.5, textAlign: 'center', pt: 1 }}>
          {t('documents.previewTruncated', { count: truncatedAt })}
        </Typography>
      )}
    </Box>
  );
};

export default PdfPreview;
